/**
 * "Show the working": every calculation records its steps here. The inspector and the report render
 * these; nothing downstream recalculates. Labels are our own words; standard content is referenced by ID.
 */

export type Unit =
  | 'kg/s'
  | 'm3/s'
  | 'm/s'
  | 'Pa'
  | 'K'
  | 'm2'
  | 'm3'
  | 'm'
  | 'kg/m3'
  | 'kg/kmol'
  | 'J/(kg·K)'
  | 'J/(kmol·K)'
  | 'vol/vol'
  | '1';

export interface Ref {
  clause: string;
  eq?: string;
  table?: string;
  figure?: string;
  /** Method decision applied (docs/method-decisions.md). */
  md?: string;
}

export interface Q {
  value: number;
  unit: Unit;
}

export interface Note {
  kind: 'assumption' | 'warning' | 'decision' | 'info';
  text: string;
  ref?: Ref;
}

export interface Step {
  id: string;
  label: string;
  ref: Ref;
  /** Keyed by symbol as used in the standard, e.g. "Cd", "S", "p". */
  inputs: Record<string, Q>;
  /** A quantity, or a classification such as "Medium" or "Zone 2". */
  output: { symbol: string } & (Q | { text: string });
  notes?: Note[];
}

export type Severity = 'error' | 'warning' | 'info';

export interface Issue {
  code: string;
  severity: Severity;
  message: string;
  ref?: Ref;
  /** Document field the issue is about, for focusing the input. */
  field?: string;
}

export class Recorder {
  readonly steps: Step[] = [];
  readonly issues: Issue[] = [];

  step(step: Step): void {
    this.steps.push(step);
  }

  issue(issue: Issue): void {
    if (!this.issues.some((i) => i.code === issue.code && i.field === issue.field)) this.issues.push(issue);
  }

  get hasErrors(): boolean {
    return this.issues.some((i) => i.severity === 'error');
  }
}

export const q = (value: number, unit: Unit): Q => ({ value, unit });
